import { useEffect, useRef } from 'react';
import { ApiError, rest, useStore } from '@tasca/client-sdk';
import type { Guild } from '@tasca/client-sdk';
import { canAssignRole } from '../lib/permissions.js';
import { Icon } from './Icon.js';
import { notify } from './Overlays.js';

/**
 * Dar e tirar permissões a uma pessoa.
 *
 * As definições do servidor fazem isto pelo lado do grupo — "quem tem estas
 * permissões" — e isso serve para arrumar dez pessoas de uma vez. Mas a
 * pergunta que se faz todos os dias é a outra: "este, o que é que ele pode?".
 * Essa faz-se em cima da pessoa, e é daí que isto se abre.
 *
 * Cada caixa aplica-se logo. Não há botão de guardar porque não há nada a
 * compor: dar um grupo é um pedido, e o GUILD_MEMBER_UPDATE que volta pelo
 * gateway é que redesenha a caixa — se o servidor recusar, a caixa não mexe e
 * o aviso diz porquê.
 */
export function MemberRoles({
  guild,
  userId,
  name,
  onClose,
}: {
  guild: Guild;
  userId: string;
  name: string;
  onClose: () => void;
}): React.ReactElement {
  const dialog = useRef<HTMLDialogElement>(null);
  const me = useStore((s) => s.me);
  const roles = useStore((s) => s.roles.get(guild.id) ?? []);
  const members = useStore((s) => s.members.get(guild.id) ?? []);

  const target = members.find((m) => m.userId === userId);
  const actor = members.find((m) => m.userId === me?.id);

  useEffect(() => {
    dialog.current?.showModal();
  }, []);

  // O @everyone não se dá nem se tira: toda a gente o tem, por definição.
  const assignable = roles.filter((r) => r.id !== guild.id);

  async function toggle(roleId: string, had: boolean): Promise<void> {
    try {
      await rest.setMemberRoles(guild.id, userId, had ? { remove: [roleId] } : { add: [roleId] });
    } catch (err) {
      notify(
        err instanceof ApiError
          ? err.code === 'role_hierarchy'
            ? 'Esse grupo está acima do teu — não o podes dar nem tirar.'
            : err.message
          : 'Falhou mudar as permissões',
        'error',
      );
    }
  }

  return (
    <dialog
      ref={dialog}
      className="ask member-roles"
      aria-label={`Permissões de ${name}`}
      onCancel={onClose}
      onClick={(e) => {
        if (e.target === dialog.current) onClose();
      }}
    >
      <form method="dialog" onSubmit={onClose}>
        <h2>Permissões de {name}</h2>

        {assignable.length === 0 ? (
          <p className="ask-body">
            Este servidor ainda não tem grupos de permissões. Cria um nas definições do servidor.
          </p>
        ) : (
          <ul className="member-roles-list">
            {assignable.map((role) => {
              const had = target?.roles.includes(role.id) ?? false;
              const allowed = canAssignRole(guild, actor, role, roles);
              return (
                <li key={role.id}>
                  <label className={allowed ? '' : 'locked'}>
                    <input
                      type="checkbox"
                      checked={had}
                      disabled={!allowed}
                      onChange={() => void toggle(role.id, had)}
                    />
                    <span
                      className="role-dot"
                      style={{
                        background: role.color === 0 ? 'var(--faint)' : `#${role.color.toString(16).padStart(6, '0')}`,
                      }}
                    />
                    {role.name}
                    {!allowed && (
                      <span className="role-locked" title="Está acima do teu grupo mais alto">
                        <Icon name="ban" />
                      </span>
                    )}
                  </label>
                </li>
              );
            })}
          </ul>
        )}

        <div className="ask-actions">
          <button type="submit">Fechar</button>
        </div>
      </form>
    </dialog>
  );
}
